import { createContext, useContext, useMemo } from "react";
import { Layer, LayerTypeProviderProps } from "../../../types";
import { useConstraintsStore } from "./store";
import type { ConstraintsStore, ConstraintsStoreDispatch } from "./store";
import { ConstraintsLayer } from "../types";
import { PermitsLayerProvider } from "../../PermitsLayerType/Context";

export const ConstraintsLayerTypeContext = createContext({} as ConstraintsLayerTypeContextProps);

export const useConstraintsLayerTypeContext = () => useContext(ConstraintsLayerTypeContext);

// Returns true of the layer in question makes use of the constraints provider
// Check for OwnershipCombinedProvider by layer ID to avoid circular dependency
export const layerUsesConstraintsProvider = (l: Layer): l is ConstraintsLayer => {
  if (!l.layerType?.Provider) return false;
  if (l.layerType.Provider === ConstraintsLayerTypeProvider) return true;
  if (l.layerType.Provider === PermitsLayerProvider) return true;
  if (l.id === "ownership-combined") return true;

  return false;
};

interface ConstraintsLayerTypeContextProps {
  constraintsStore: ConstraintsStore;
  dispatch: ConstraintsStoreDispatch;
}

export function ConstraintsLayerTypeProvider({ id, children, layers, isWorkbench }: LayerTypeProviderProps) {
  const LOCAL_STORAGE_KEY = `${id}-constraints-bundle`;
  const constraintsLayers = useMemo(
    () =>
      layers
        .filter(layerUsesConstraintsProvider)
        .map((layer) => (isWorkbench ? applyDefaultVisibility(layer, true) : layer)),
    [isWorkbench, layers]
  );
  const [constraintsStore, dispatch] = useConstraintsStore(LOCAL_STORAGE_KEY, constraintsLayers);

  return (
    <ConstraintsLayerTypeContext.Provider
      value={{
        dispatch,
        constraintsStore,
      }}
    >
      {children}
    </ConstraintsLayerTypeContext.Provider>
  );
}

function applyDefaultVisibility(layer: ConstraintsLayer, value: boolean) {
  if (!layer.layerConfig) return layer;

  return {
    ...layer,
    layerConfig: {
      ...layer.layerConfig,
      categorySchema: layer.layerConfig.categorySchema.map((category) => ({
        ...category,
        defaultValue: value,
      })),
    },
  };
}
